import React, { useState, useEffect, useRef } from 'react';
import './Home.css';

// Composant pour les compteurs animés
const AnimatedCounter = ({ target, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const counterRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          let start = 0;
          const increment = target / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) observer.observe(counterRef.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <div ref={counterRef} className="stat-number">
      {count}{suffix}
    </div>
  );
};

// Composant Skill Card
const SkillCard = ({ icon, title, description, techs = [] }) => (
  <div className="skill-card">
    <div className="skill-card-icon">{icon}</div>
    <h4>{title}</h4>
    <p>{description}</p>
    {techs.length > 0 && (
      <div className="skill-techs">
        {techs.map((tech, idx) => (
          <span key={idx} className="tech-tag">{tech}</span>
        ))}
      </div>
    )}
  </div>
);

// Composant Service Card
const ServiceCard = ({ emoji, title, description, features = [] }) => (
  <div className="service-card">
    <div className="service-card-emoji">{emoji}</div>
    <h4>{title}</h4>
    <p>{description}</p>
    <ul className="service-features">
      {features.map((feature, idx) => (
        <li key={idx}>{feature}</li>
      ))}
    </ul>
    <a href="/services" className="service-link">
      En savoir plus →
    </a>
  </div>
);

// Composant Testimonial Card
const TestimonialCard = ({ text, author, role, rating = 5 }) => (
  <div className="testimonial-card">
    <div className="testimonial-quote">"</div>
    <p>{text}</p>
    <div className="testimonial-rating">
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </div>
    <div className="testimonial-author">
      <div className="testimonial-avatar">{author.charAt(0)}</div>
      <div>
        <h6>{author}</h6>
        <span>{role}</span>
      </div>
    </div>
  </div>
);

function Home() {
  const [activeNav, setActiveNav] = useState('home');
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'skills', 'about', 'services', 'testimonials'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveNav(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="home-page">
      {/* Navigation Flottante */}
      <nav className="floating-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-text">RA</span>
            <div className="logo-glow"></div>
          </div>
          <div className="nav-links">
            {['home', 'skills', 'about', 'services', 'testimonials'].map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                className={`nav-link ${activeNav === section ? 'active' : ''}`}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
          </div>
          <button className="nav-cta" onClick={() => scrollToSection('contact')}>
            Contact
          </button>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section id="home" className="hero-section" ref={heroRef}>
        <div className="hero-bg-gradient"></div>
        <div className="hero-particles">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${5 + Math.random() * 10}s`
            }}></div>
          ))}
        </div>
        
        <div className="hero-container">
          <div className="hero-content-left">
            <div className="hero-badge">
              <span className="badge-dot"></span>
              Disponible pour mission
            </div>
            <h1 className="hero-title">
              Créateur d'expériences
              <span className="title-gradient"> web innovantes</span>
            </h1>
            <div className="hero-typed">
              <span className="typed-text">React - Laravel</span>
              <span className="typed-cursor">|</span>
            </div>
            <p className="hero-description">
              Je transforme vos idées en applications web performantes, 
              avec une expertise pointue en React, Laravel et UI/UX design.
              <strong> 4+ ans d'expérience</strong> à donner vie à des projets digitaux.
            </p>
            <div className="hero-cta-group">
              <button className="btn-primary-glow" onClick={() => scrollToSection('contact')}>
                Commencer un projet
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <button className="btn-outline-cyan" onClick={() => window.location.href = '/portfolio'} >
                Voir mon travail
              </button>
            </div>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="stat-value">4+</span>
                <span className="stat-label">Années d'expérience</span>
              </div>
              <div className="hero-stat-divider"></div>
              <div className="hero-stat">
                <span className="stat-value">50+</span>
                <span className="stat-label">Projets livrés</span>
              </div>
              <div className="hero-stat-divider"></div>
              <div className="hero-stat">
                <span className="stat-value">100%</span>
                <span className="stat-label">Satisfaction client</span>
              </div>
            </div>
          </div>
          
          <div className="hero-content-right">
            <div className="hero-image-container">
              <div className="hero-image-glow"></div>
              <img src="./images/myphoto.png" alt="Rodrigue APOTHEY" />
              <div className="hero-image-tech">
                 <div className="tech-badge laravel">Laravel</div>
                <div className="tech-badge react">React</div>               
                <div className="tech-badge figma">Tailwindcss</div>
                 <div className="tech-badge laravel">Php</div>
                  <div className="tech-badge laravel">Jsvanilla</div>

              </div>
            </div>
          </div>
        </div>
        
        <div className="hero-scroll-indicator">
          <div className="scroll-mouse"></div>
          <div className="scroll-arrows">
            <span></span>
            <span></span>
          </div>
        </div>
      </section>

      {/* SKILLS SECTION */}
{/* SKILLS SECTION */}
<section id="skills" className="skills-section">
  <div className="section-container">
    <div className="section-header">
      <span className="section-badge">Expertise technique</span>
      <h2 className="section-title">Technologies <span className="title-accent">maîtrisées</span></h2>
      <p className="section-subtitle">Des stacks modernes pour des performances optimales</p>
    </div>
    
    <div className="skills-grid">
      <SkillCard 
        icon="⚛️"
        title="React & Ecosystem"
        description="Applications SPA performantes avec hooks, context, Redux et Next.js"
        techs={['React 18', 'Next.js', 'Redux', 'Tailwind']}
      />
      <SkillCard 
        icon="🐘"
        title="PHP & Laravel"
        description="APIs RESTful, systèmes d'authentification et architectures MVC"
        techs={['Laravel 10', 'PHP 8', 'MySQL', 'REST API']}
      />
      <SkillCard 
        icon="🎨"
        title="UI/UX Integration"
        description="Intégration pixel-perfect de maquettes Figma, XD et Sketch"
        techs={['Figma', 'Adobe XD', 'Responsive', 'Animations']}
      />
      <SkillCard 
        icon="🗄️"
        title="Base de données"
        description="Conception et optimisation de bases de données relationnelles"
        techs={['MySQL', 'PostgreSQL', 'MongoDB', 'Prisma']}
      />
      
      {/* NOUVEAU : Automatisation & Workflows */}
      <SkillCard 
        icon="⚙️"
        title="Automatisation & Workflows"
        description="Optimisation des processus de développement et déploiement continu"
        techs={['CI/CD', 'GitHub Actions', 'Docker', 'Automation']}
      />
      
      {/* NOUVEAU : Intelligence Artificielle */}
      <SkillCard 
        icon="🤖"
        title="IA & Intégration"
        description="Intégration de modèles IA et automatisation intelligente"
        techs={['OpenAI API', 'Machine Learning', 'Chatbots', 'AI Agents']}
      />
    </div>
  </div>
</section>

      {/* ABOUT SECTION */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-grid">
            <div className="about-content">
              <span className="section-badge">Qui suis-je ?</span>
              <h2 className="section-title">Passionné par le <span className="title-accent">développement web</span></h2>
              <p className="about-text">
                <strong>Rodrigue Elie K.T. APOTHEY</strong>, <strong>Ingénieur Full-Stack</strong> spécialisé 
                <strong> Laravel & React</strong>. Avec plus de <strong>4 ans d'expérience</strong>, je conçois des 
                <strong>applications web robustes et évolutives</strong> : <strong>e-commerce, SaaS, PWA, applications métier</strong>.
                <br /><br />
                Ma véritable force ? Mon <strong>esprit d'analyse</strong>. Je ne me contente pas de coder : 
                j'<strong>analyse vos besoins</strong>, je <strong>structure les données</strong> et je construis des 
                <strong>solutions sur mesure, de zéro</strong>, en suivant les principes du 
                <strong>Data-Driven Development</strong>.
                <br /><br />
                Aujourd'hui, j'intègre <strong>l'IA et l'automatisation des workflows</strong> pour pousser 
                la performance et l'innovation encore plus loin.
              </p>
              <div className="about-highlights">
                <div className="highlight-item">
                  <div className="highlight-icon">🎯</div>
                  <div>+50 projets réalisés avec succès</div>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">🚀</div>
                  <div>Performance web optimisée</div>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">📱</div>
                  <div>100% responsive design</div>
                </div>
              </div>
              <div className="about-stats">
                <div className="about-stat">
                  <AnimatedCounter target={4} suffix="+" />
                  <span>Années d'xp</span>
                </div>
                <div className="about-stat">
                  <AnimatedCounter target={50} suffix="+" />
                  <span>Projets</span>
                </div>
                <div className="about-stat">
                  <AnimatedCounter target={30} suffix="+" />
                  <span>Clients</span>
                </div>
              </div>
            </div>
            <div className="about-image-wrapper">
              <div className="about-image-glow"></div>
              <img src="./images/about.jpg" alt="À propos" />
              <div className="about-experience-badge">
                <span>4+</span>
                <p>Ans d'expérience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section id="services" className="services-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">Ce que je propose</span>
            <h2 className="section-title">Services <span className="title-accent">sur mesure</span></h2>
            <p className="section-subtitle">Des solutions adaptées à vos besoins spécifiques</p>
          </div>
          
          <div className="services-grid">
            <ServiceCard 
              emoji="💻"
              title="Développement Web"
              description="Création d'applications web robustes et évolutives"
              features={['React/Next.js SPA', 'APIs Laravel', 'Base de données optimisée', 'Déploiement CI/CD']}
            />
            <ServiceCard 
              emoji="🎨"
              title="Intégration UI/UX"
              description="Transformation de vos designs en code parfait"
              features={['Pixel-perfect Figma', 'Animations fluides', 'Responsive mobile-first', 'Accessibilité WCAG']}
            />
            <ServiceCard 
              emoji="📚"
              title="Formation & Coaching"
              description="Transmission de compétences et accompagnement technique"
              features={['Formation React', 'Mentorat développeurs', 'Code review', 'Bonnes pratiques']}
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">Témoignages</span>
            <h2 className="section-title">Ce que disent <span className="title-accent">mes clients</span></h2>
            <p className="section-subtitle">Ils me font confiance pour leurs projets</p>
          </div>
          
          <div className="testimonials-grid">
            <TestimonialCard 
              text="Un développeur exceptionnel ! Rodrigue a su comprendre parfaitement nos besoins et proposer des solutions techniques innovantes. L'application est fluide, moderne et les délais ont été respectés."
              author="Jean-Marc L."
              role="CEO, TechStart"
              rating={5}
            />
            <TestimonialCard 
              text="Très bonne collaboration. Rodrigue est à l'écoute, proactif et maîtrise parfaitement son sujet. L'intégration de nos maquettes Figma est impeccable. Je recommande vivement !"
              author="Sarah K."
              role="Directrice Créative"
              rating={5}
            />
            <TestimonialCard 
              text="Professionnalisme et qualité sont au rendez-vous. Rodrigue a développé une application Laravel/React qui répond parfaitement à nos enjeux métier. Un vrai plus pour notre entreprise."
              author="Thomas D."
              role="CTO, InnovCorp"
              rating={5}
            />
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section id="contact" className="cta-section">
        <div className="section-container">
          <div className="cta-card">
            <h2 className="cta-title">Prêt à concrétiser <span className="title-accent">votre projet ?</span></h2>
            <p className="cta-text">
              Discutons de vos idées et voyons comment je peux vous aider à les réaliser.
              Une première rencontre, ça ne coûte rien 😊
            </p>
            <div className="cta-buttons">
              <button className="btn-primary-3d" onClick={() => window.location.href = '/contact'}>
                Démarrer un projet
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <button className="btn-secondary" onClick={() => window.location.href = '/portfolio'}>
                Voir mon portfolio
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-info">
              <h3>Rodrigue APOTHEY</h3>
              <p>Frontend React Developer & UI/UX Integrator</p>
            </div>
            <div className="footer-links">
              <a href="#home">Accueil</a>
              <a href="#skills">Compétences</a>
              <a href="#about">À propos</a>
              <a href="#services">Services</a>
              <a href="#contact">Contact</a>
            </div>
            <div className="footer-social">
              <a href="https://github.com/eliletishbit" className="social-link">GitHub</a>
              <a href="https://www.linkedin.com/in/kossi-apothey-6b9a19268/" className="social-link">LinkedIn</a>
              <a href="#" className="social-link">Twitter</a>
            </div>
          </div>
          <div className="footer-copyright">
            <p>&copy; 2024 Rodrigue APOTHEY. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;